import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, CheckCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { getLookupKey, SubscriptionTier } from "@/lib/subscription";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const passwordSchema = z.string().min(6, "Password must be at least 6 characters").max(72);

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, session, isLoading: authLoading, updatePassword } = useAuth();
  const [statusMessage, setStatusMessage] = useState("Confirming your email...");
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);

  useEffect(() => {
    // Check if this is a password recovery callback
    const type = searchParams.get("type");
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const hashType = hashParams.get("type");
    
    if (type === "recovery" || hashType === "recovery") {
      setIsRecoveryMode(true);
      return;
    }

    const handleCallback = async () => {
      if (authLoading) return;
      
      if (!user || !session) {
        navigate("/auth", { replace: true });
        return;
      }

      // Read all signup data from profiles (single source of truth)
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('signup_plan, signup_billing, paid_signup_checkout_initiated')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Failed to read profile:', profileError);
        toast.error('Something went wrong. Redirecting to dashboard...');
        navigate("/dashboard", { replace: true });
        return;
      }

      const signupPlan = profileData?.signup_plan;
      const isPaidPlan = signupPlan && ['starter', 'pro', 'unlimited'].includes(signupPlan);

      // Free/null guard: skip all Stripe logic
      if (!isPaidPlan) {
        navigate("/dashboard", { replace: true });
        return;
      }

      // Already-initiated guard
      if (profileData?.paid_signup_checkout_initiated) {
        toast.info("We started your subscription — visit Plans to complete it.");
        navigate("/dashboard", { replace: true });
        return;
      }

      // Atomic claim: prevent duplicate Stripe sessions
      const { data: claimResult } = await supabase.rpc('claim_checkout_session', {
        _user_id: user.id,
      });

      if (!claimResult) {
        // Lost the race — another tab already claimed
        toast.info("Checkout already in progress in another tab.");
        navigate("/dashboard", { replace: true });
        return;
      }

      // Billing fallback: default to 'monthly' if null or invalid
      const rawBilling = profileData?.signup_billing;
      const billing: 'monthly' | 'annual' = 
        rawBilling === 'monthly' || rawBilling === 'annual' ? rawBilling : 'monthly';

      setStatusMessage("Setting up your subscription...");
      
      const lookupKey = getLookupKey(signupPlan as SubscriptionTier, billing);
      
      try {
        const { data, error } = await supabase.functions.invoke('create-checkout', {
          body: { lookupKey },
        });

        if (error) {
          console.error('Checkout error:', error);
          toast.error('Failed to start checkout. Redirecting to plans...');
          navigate("/plans", { replace: true });
          return;
        }

        if (data?.url) {
          window.location.href = data.url;
          return;
        }
      } catch (error) {
        console.error('Checkout error:', error);
        toast.error('Failed to start checkout. Redirecting to plans...');
        navigate("/plans", { replace: true });
        return;
      }
    };

    handleCallback();
  }, [user, session, authLoading, navigate, searchParams]);

  const validatePasswordForm = () => {
    const newErrors: Record<string, string> = {};

    const passwordResult = passwordSchema.safeParse(newPassword);
    if (!passwordResult.success) {
      newErrors.newPassword = passwordResult.error.errors[0].message;
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordForm()) return;

    setIsLoading(true);

    try {
      const { error } = await updatePassword(newPassword);
      if (error) {
        toast.error(error.message);
      } else {
        setPasswordUpdated(true);
        toast.success("Password updated successfully!");
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 2000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Password recovery mode - show password update form
  if (isRecoveryMode) {
    return (
      <div className="min-h-screen flex flex-col bg-background grain">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6 py-16 md:py-24">
          <div className="w-full max-w-md">
            <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
              {passwordUpdated ? (
                // Success state
                <>
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-accent" />
                    </div>
                  </div>
                  <h1 className="font-display text-2xl font-bold text-foreground text-center mb-2">
                    Password updated!
                  </h1>
                  <p className="text-muted-foreground text-center mb-6">
                    Your password has been successfully updated. Redirecting to dashboard...
                  </p>
                  <div className="flex justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-accent" />
                  </div>
                </>
              ) : (
                // Password update form
                <>
                  <h1 className="font-display text-2xl font-bold text-foreground text-center mb-2">
                    Set your new password
                  </h1>
                  <p className="text-muted-foreground text-center mb-6">
                    Enter a new password for your account.
                  </p>

                  <form onSubmit={handlePasswordUpdate} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">New password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="newPassword"
                          type="password"
                          placeholder="••••••••"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="pl-10"
                          autoComplete="new-password"
                        />
                      </div>
                      {errors.newPassword && (
                        <p className="text-destructive text-sm">{errors.newPassword}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="confirmPassword"
                          type="password"
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="pl-10"
                          autoComplete="new-password"
                        />
                      </div>
                      {errors.confirmPassword && (
                        <p className="text-destructive text-sm">{errors.confirmPassword}</p>
                      )}
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Update password"
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent mx-auto mb-4" />
        <p className="text-muted-foreground">{statusMessage}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
